// Payroll periods — closing a month, and reopening one for two hours when
// something in it has to change.
//
// Every rule shown here is enforced in Postgres
// (20260829100000_period_lock.sql). The button states below exist so a person is
// not offered an action the database will refuse; when one is refused anyway,
// the message rendered is the database's own, because it already says what to do
// instead ("cannot lock August 2026 — the month has not ended yet").
//
// Locking and amending are the two gravest acts in the product, and both went
// through window.confirm / window.prompt — a 70-word browser box whose focused
// default was OK, and a prompt whose blank answer returned in silence (HRA-08).
// Lock is rung 3 of the stakes ladder (UI-CONVENTIONS §5): irreversible, so
// type-to-confirm. An amendment is rung 2: its written reason is the record.
//
// Locking is one action, "Lock through <month>", not a Lock on every open
// month: four months behind was four buttons and four confirms, and the
// later ones stood down the card as ghost labels (critique 2026-10-06 #5).
// It locks every ended open month up to the one chosen, oldest first, behind
// one type-to-confirm that lists them, and stops at the first refusal, so a
// month is never locked over an open one before it.
import { useCallback, useEffect, useState } from 'react';
import { Icon, Badge, Button, Mono, Select } from './primitives';
import { PCard } from './people';
import { ConfirmDialog } from './dialog';
import { Field } from './field';
import { InlineStatus, LoadError, useToast } from './feedback';
import {
  listPeriods,
  listAmendments,
  lockPeriod,
  amendPeriod,
  periodKey,
  recentMonths,
  monthHasEnded,
  monthLabel,
  clockTime,
  windowOpenUntil,
  AMEND_WINDOW_MS,
  type Period,
  type PeriodStatus,
  type Amendment,
} from '../lib/payrollPeriods';

// Neutral, not amber: amber is the queue tone (UI-CONVENTIONS §1), and a locked
// month is not waiting on anyone — it is finished. The lock glyph says locked;
// only an OPEN amendment window, which does run out, is called out below.
const STATUS_TONE: Record<PeriodStatus, 'neutral' | 'success'> = {
  open: 'neutral',
  locked: 'neutral',
  amended: 'neutral',
  paid: 'success',
};

type Month = { year: number; month: number };

/** "May 2026", "May 2026 and June 2026", "May 2026, June 2026 and July 2026". */
const listOf = (months: readonly Month[]) => {
  const names = months.map((m) => monthLabel(m.year, m.month));
  const last = names.pop();
  return names.length ? `${names.join(', ')} and ${last}` : (last ?? '');
};
// At module scope, so the dialogs inside are not remounted on every render.
// Bare with `only` (one month beside the register): no card, so the header's
// Lock through stands above the month instead of in a card header.
const Frame = ({
  bare,
  right,
  children,
}: {
  bare: boolean;
  right?: React.ReactNode;
  children: React.ReactNode;
}) =>
  bare ? (
    <div>
      {right && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--hf-space-2)' }}
        >
          {right}
        </div>
      )}
      {children}
    </div>
  ) : (
    <PCard label="Payroll periods" right={right}>
      {children}
    </PCard>
  );

export function PayrollPeriods({
  canLock,
  onChanged,
  onAmendmentOpen,
  only,
}: {
  canLock: boolean;
  /**
   * One month's actions, with no card around them: the payroll register shows
   * the month and its status already, so a second six-month card beside it
   * repeated both (bug report 2026-10-05: "the Month payroll amendment section
   * is redundant to the Payroll Summary … better if both were combined").
   */
  only?: Month;
  onChanged?: () => void;
  /**
   * A write window is open on this month — either just filed, or already running.
   * The caller opens the workspace; this component has no person, no org hours
   * and no leave data in scope, and threading four props through it to reach one
   * modal would be the wrong shape.
   */
  onAmendmentOpen?: (year: number, month: number, closesAt: Date) => void;
}) {
  const toast = useToast();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  // A failed read is not "six open months": with no rows every month reads as
  // open and offers Lock on one that is already locked.
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The month the lock runs through, while its confirm is open.
  const [locking, setLocking] = useState<string | null>(null);
  const [amending, setAmending] = useState<Month | null>(null);

  const reload = useCallback(async () => {
    const [p, a] = await Promise.all([listPeriods(), listAmendments()]);
    setLoadFailed(!!(p.error || a.error));
    setPeriods(p.data);
    setAmendments(a.data);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const after = async (run: () => Promise<{ error: { message: string } | null }>, key: string) => {
    setBusy(key);
    setErr(null);
    const { error } = await run();
    if (error) setErr(error.message);
    await reload();
    onChanged?.();
    setBusy(null);
    return { error };
  };

  // Oldest first, one at a time, and no further after a refusal: the report
  // says what locked, what was refused and why, and what is still open.
  const lockThrough = async (months: readonly Month[]) => {
    setLocking(null);
    setBusy('lock');
    setErr(null);
    const locked: Month[] = [];
    let refused: { at: Month; message: string } | null = null;
    for (const m of months) {
      const { error } = await lockPeriod(m.year, m.month);
      if (error) {
        refused = { at: m, message: error.message };
        break;
      }
      locked.push(m);
    }
    if (refused) {
      const left = months.slice(locked.length + 1);
      setErr(
        [
          locked.length ? `Locked ${listOf(locked)}.` : '',
          `Couldn’t lock ${listOf([refused.at])}: ${refused.message.replace(/\.?$/, '.')}`,
          left.length ? `${listOf(left)} ${left.length > 1 ? 'are' : 'is'} still open.` : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    } else toast.push({ tone: 'success', message: `Locked ${listOf(locked)}.` });
    await reload();
    onChanged?.();
    setBusy(null);
  };

  const amend = ({ year, month }: Month, reason: string) => {
    setAmending(null);
    void after(() => amendPeriod(year, month, reason), periodKey(year, month)).then(({ error }) => {
      // Only on success. `after` surfaces a refusal into `err` rather than
      // throwing, so an unguarded .then() would open a workspace for a window
      // that was never opened. Filing the exception IS opening the window
      // (period_lock.sql:66-71), so the two hours are already running here.
      if (!error) onAmendmentOpen?.(year, month, new Date(Date.now() + AMEND_WINDOW_MS));
    });
  };

  // Every month the register knows about, plus the recent ones it does not:
  // absence of a row means open, so a month nobody has closed still needs a line.
  const known = new Map(periods.map((p) => [periodKey(p.year, p.month), p]));
  const rowOf = ({ year, month }: Month) => {
    const key = periodKey(year, month);
    return {
      key,
      year,
      month,
      status: (known.get(key)?.status ?? 'open') as PeriodStatus,
      openUntil: windowOpenUntil(key, amendments),
    };
  };
  const rows = (only ? [only] : recentMonths(6)).map(rowOf);

  // The months the one action can lock: ended and still open, oldest first,
  // from the last six. By default it runs through the newest of them; the
  // confirm can stop it sooner. The database locks any ended month in any
  // order (enforce_payroll_period, 20260829100000); oldest first is this
  // card's rule, so a later month is never locked over an earlier open one.
  // Beside the register (`only`) the action belongs to the month shown and runs
  // through it, so "Lock through September" takes an open August with it. A
  // month stepped back past the six is its own window.
  const onlyKey = only && periodKey(only.year, only.month);
  const span = recentMonths(6).map(rowOf);
  if (onlyKey && onlyKey < span[span.length - 1].key) span.push(rows[0]);
  const lockable = [...span]
    .reverse()
    .filter(
      (r) =>
        r.status === 'open' && monthHasEnded(r.year, r.month) && (!onlyKey || r.key <= onlyKey),
    );
  const newest = lockable[lockable.length - 1];
  // With `only`, offered on the month shown or not at all.
  const offered = onlyKey && newest?.key !== onlyKey ? undefined : newest;
  const through = lockable.findIndex((r) => r.key === locking);
  const toLock = lockable.slice(0, through + 1);
  // ponytail: the run sees the last six months, not the register's whole past.
  // An ended month older than them, still open, is neither listed nor locked,
  // so when the run starts at the window's oldest month the confirm says so.
  // Reading the oldest open month from the data would close it.
  const oldest = span[span.length - 1];
  const fromEdge = toLock[0]?.key === oldest.key;
  const lockLabel = through >= 0 ? monthLabel(lockable[through].year, lockable[through].month) : '';
  const amendLabel = amending ? monthLabel(amending.year, amending.month) : '';

  return (
    <Frame
      bare={!!only}
      right={
        canLock &&
        !loadFailed &&
        offered && (
          // Secondary, not danger: rose spends the colour for "wrong" on a
          // routine month-end act. The weight is in the confirm, where the
          // consequences are read. A phone task too, so .hf-hit (§8).
          <Button
            variant="secondary"
            size="sm"
            icon="lock"
            className="hf-hit"
            loading={busy === 'lock'}
            onClick={() => setLocking(offered.key)}
          >
            Lock through {monthLabel(offered.year, offered.month)}
          </Button>
        )
      }
    >
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}

      {loadFailed ? (
        <LoadError what="the payroll periods" onRetry={() => void reload()} />
      ) : (
        rows.map((r) => {
          const ended = monthHasEnded(r.year, r.month);
          // A running month says so under it. A month that has ended says
          // where it stands: open (the header's lock takes it), or locked,
          // amended or paid. With `only`, the register beside it already
          // badges the month.
          const badged = !only && (r.status !== 'open' || ended);
          return (
            <div
              key={r.key}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--hf-space-2) var(--hf-space-3)',
                padding: 'var(--hf-space-2) 0',
                borderTop: '1px solid var(--hf-rule)',
              }}
            >
              {!only && (
                <Mono
                  style={{
                    fontSize: 'var(--hf-text-sm)',
                    color: 'var(--hf-ink)',
                    minWidth: '9rem',
                  }}
                >
                  {monthLabel(r.year, r.month)}
                </Mono>
              )}

              {(badged || r.openUntil || !ended) && (
                <span
                  style={{
                    flex: '1 1 12rem',
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 'var(--hf-space-2)',
                    minWidth: 0,
                  }}
                >
                  {badged && (
                    <Badge tone={STATUS_TONE[r.status]} dot={r.status === 'paid'}>
                      {r.status !== 'open' && r.status !== 'paid' && <Icon name="lock" size={10} />}
                      {r.status}
                    </Badge>
                  )}
                  {r.openUntil && (
                    <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-amber-text)' }}>
                      amendment open until {clockTime(r.openUntil)}
                    </span>
                  )}
                  {!ended && (
                    <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                      still running — can be locked once it ends
                    </span>
                  )}
                </span>
              )}

              {/* Month-end is a phone task too: each action is a small text
                  Button, so it carries .hf-hit (UI-CONVENTIONS §8). Side by
                  side they only grow in height, inside the row's padding. */}
              <span
                style={{ display: 'inline-flex', gap: 'var(--hf-space-2)', marginLeft: 'auto' }}
              >
                {canLock && r.openUntil && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="hf-hit"
                    ariaLabel={`Open the amendment workspace for ${monthLabel(r.year, r.month)}`}
                    onClick={() => onAmendmentOpen?.(r.year, r.month, r.openUntil!)}
                  >
                    Open workspace
                  </Button>
                )}
                {canLock && r.status !== 'open' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="hf-hit"
                    loading={busy === r.key}
                    ariaLabel={`${r.openUntil ? 'Re-open' : 'Amend'} ${monthLabel(r.year, r.month)}`}
                    onClick={() => setAmending({ year: r.year, month: r.month })}
                  >
                    {/* Filing a second amendment is legitimate — it re-opens the
                        window — but each one is recorded against the period
                        forever, so the already-open case gets its own button
                        above rather than being reached through this one. */}
                    {r.openUntil ? 'Re-open' : 'Amend'}
                  </Button>
                )}
              </span>
            </div>
          );
        })
      )}

      <ConfirmDialog
        open={through >= 0}
        title={`Lock through ${lockLabel} for payroll?`}
        tone="danger"
        consequence={
          <>
            {/* Stopping sooner is a choice made here, beside the list it
                changes: the month typed below follows it. */}
            {lockable.length > 1 && (
              <Field label="Lock through" style={{ marginBottom: 'var(--hf-space-3)' }}>
                {(control) => (
                  <Select
                    {...control}
                    value={locking ?? ''}
                    onChange={setLocking}
                    options={lockable.map((r) => ({
                      value: r.key,
                      label: monthLabel(r.year, r.month),
                    }))}
                  />
                )}
              </Field>
            )}
            <ul style={{ margin: 0, paddingLeft: 'var(--hf-space-5)' }}>
              <li>
                {toLock.length > 1
                  ? `Locks ${toLock.length} months, oldest first: ${listOf(toLock)}.`
                  : `Locks ${lockLabel}.`}
              </li>
              {fromEdge && (
                <li>
                  Months before {monthLabel(oldest.year, oldest.month)} aren’t included, and stay as
                  they are.
                </li>
              )}
              <li>
                Time entries, attendance and leave inside {toLock.length > 1 ? 'them' : 'it'} become
                read-only for every role, including you.
              </li>
              <li>
                Locking credits {toLock.length > 1 ? 'each month’s' : `${lockLabel}’s`} overtime as
                comp off, in half days, to everyone who earned it.
              </li>
              <li>
                A locked month never reopens. The only way to change one afterwards is an amendment,
                which is recorded with a reason.
              </li>
              <li>This cannot be undone.</li>
            </ul>
          </>
        }
        confirmLabel={`Lock through ${lockLabel}`}
        typeToConfirm={lockLabel}
        onCancel={() => setLocking(null)}
        onConfirm={() => void lockThrough(toLock)}
      />
      <ConfirmDialog
        open={amending !== null}
        title={`Amend ${amendLabel}?`}
        consequence={[
          `Opens a two-hour window in which admins can edit ${amendLabel}.`,
          'The reason and your name are recorded against the period, permanently.',
        ]}
        confirmLabel="Open the window"
        requireReason={{ label: `Why does ${amendLabel} need to change?`, minLength: 3 }}
        onCancel={() => setAmending(null)}
        onConfirm={(reason) => amending && reason && amend(amending, reason)}
      />
    </Frame>
  );
}
